import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/shared/ui/button";
import type { Tag } from "@/services/api/tags";
import { DEFAULT_TAG_COLOR } from "@/services/api/tags";
import { TagColorPalette } from "./TagColorPalette";
import { cn } from "@/shared/lib/cn";

interface TagsTableRowProps {
  tag: Tag;
  onEdit: (tag: Tag) => void;
  onDelete: (tag: Tag) => void;
  onColorChange: (tag: Tag, color: string) => void;
}

export function TagsTableRow({
  tag,
  onEdit,
  onDelete,
  onColorChange,
}: TagsTableRowProps) {
  const filesCount = (tag as any).filesCount ?? 0;
  const isAiGenerated = tag.type === "AiGenerated";

  return (
    <tr
      className={cn(
        "transition-colors",
        isAiGenerated ? "bg-grid-50/50 hover:bg-grid-50/70" : "hover:bg-grid-50"
      )}
    >
      <td className="px-6 py-4">
        <div className="font-medium text-ink-900">
          {tag.name}
          {isAiGenerated && (
            <span className="ml-2 text-xs text-ink-500 italic">(AI Tag)</span>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        <TagColorPalette
          value={tag.color ?? DEFAULT_TAG_COLOR}
          onChange={(color) => onColorChange(tag, color)}
          disabled={isAiGenerated}
        />
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-ink-700">{filesCount}</span>
      </td>
      <td className="px-6 py-4">
        <span className="inline-flex items-center gap-2 text-sm text-ink-700">
          <span
            className={`h-2 w-2 rounded-full ${
              tag.isActive ? "bg-emerald-500" : "bg-grid-300"
            }`}
          />
          {tag.isActive ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(tag)}
            disabled={isAiGenerated}
            className="h-8 w-8"
            title={
              isAiGenerated ? "AI-generated tags cannot be edited" : "Edit"
            }
          >
            <Pencil className="h-4 w-4" />
            <span className="sr-only">Edit</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(tag)}
            disabled={isAiGenerated}
            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              isAiGenerated ? "AI-generated tags cannot be deleted" : "Delete"
            }
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Delete</span>
          </Button>
        </div>
      </td>
    </tr>
  );
}
